import React from "react";
import { IPagination } from "../../data/AppInterface";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import { prepareMessageFromParams } from "../../utils/AppFunctions";
import { PER_PAGE_ROW_LIST } from "../../config/AppConfig";
import {
  chevronLeft,
  chevronRight,
  listIcon,
} from "../../assets/icons/SvgIconList";
import { TOnChangeSelect } from "../../data/AppType";
import { cn } from "../../lib";

interface IPaginationFooter {
  pagination: IPagination;
  handleChangePerPageRows?: (perPageRows: number) => void;
  handleChangePage?: (page: number) => void;
}

const PaginationFooter = ({
  pagination,
  handleChangePerPageRows,
  handleChangePage,
}: IPaginationFooter) => {
  const { t } = UseTranslationHook();
  const pageClass = "border flex items-center justify-center px-3 h-8";

  const onChangePerPageRows = (event: TOnChangeSelect) => {
    if (handleChangePerPageRows) {
      pagination.perPageRows = Number(event.target.value);
      handleChangePerPageRows(Number(event.target.value));
    }
  };
  const onChangePage = (page: number) => {
    if (handleChangePage) {
      pagination.currentPage = page;
      handleChangePage(page);
    }
  };
  const renderPaginationButtons = () => {
    if (pagination.currentPage === 1) {
      return (
        <>
          {paginationBtnView(1)}
          {paginationBtnView(2)}
          {paginationBtnView(3)}
          {paginationBtnView(4)}
          {paginationBtnView(5)}
          {dotBtnView}
          {paginationBtnView(pagination.totalPages)}
        </>
      );
    } else if (pagination.currentPage === pagination.totalPages) {
      return (
        <>
          {paginationBtnView(1)}
          {dotBtnView}
          {paginationBtnView(pagination.totalPages - 4)}
          {paginationBtnView(pagination.totalPages - 3)}
          {paginationBtnView(pagination.totalPages - 2)}
          {paginationBtnView(pagination.totalPages - 1)}
          {paginationBtnView(pagination.totalPages)}
        </>
      );
    } else {
      return (
        <>
          {paginationBtnView(1)}
          {pagination.currentPage > 3 && dotBtnView}
          {pagination.currentPage > pagination.totalPages - 3 &&
            paginationBtnView(pagination.totalPages - 4)}
          {pagination.currentPage > pagination.totalPages - 2 &&
            paginationBtnView(pagination.totalPages - 3)}
          {pagination.currentPage > 2 &&
            paginationBtnView(pagination.currentPage - 1)}
          {paginationBtnView(pagination.currentPage)}
          {pagination.currentPage < pagination.totalPages - 1 &&
            paginationBtnView(pagination.currentPage + 1)}
          {pagination.currentPage < 4 &&
            paginationBtnView(pagination.currentPage + 2)}
          {pagination.currentPage < 3 &&
            paginationBtnView(pagination.currentPage + 3)}
          {pagination.currentPage < 2 &&
            paginationBtnView(pagination.currentPage + 4)}
          {pagination.currentPage < pagination.totalPages - 2 && dotBtnView}
          {paginationBtnView(pagination.totalPages)}
        </>
      );
    }
  };
  const getEndIndex = (pageEndIndex: number, totalItems: number): string => {
    const endIndex = pageEndIndex < totalItems ? pageEndIndex : totalItems;
    return endIndex.toString();
  };
  const getPaginationMessage = () => {
    const startIndex =
      pagination.totalItems > 0
        ? ((pagination.currentPage - 1) * pagination.perPageRows + 1).toString()
        : "0";

    const pageEndIndex = pagination.currentPage * pagination.perPageRows;
    const endIndex =
      pagination.totalItems > 0
        ? getEndIndex(pageEndIndex, pagination.totalItems)
        : "0";
    return prepareMessageFromParams(t("pagination_showing"), [
      ["startIndex", startIndex],
      ["endIndex", endIndex],
      ["totalEntries", pagination.totalItems.toString()],
    ]);
  };

  const paginationBtnView = (page: number, key?: string) => {
    return (
      <button
        onClick={() => onChangePage(page)}
        disabled={pagination.currentPage === page}
      >
        <span
          className={cn(
            `${pageClass} hover:bg-accent-muted hover:text-accent rounded-md`,
            {
              "!bg-accent-emphasis !text-on-accent":
                pagination.currentPage === page,
              "bg-base": pagination.currentPage !== page,
            }
          )}
        >
          {page}
        </span>
      </button>
    );
  };

  const dotBtnView = (
    <button>
      <span className={`${pageClass} bg-base cursor-default`}>...</span>
    </button>
  );

  const prevBtnView = (
    <button
      onClick={() =>
        pagination.currentPage !== 1
          ? onChangePage(pagination.currentPage - 1)
          : {}
      }
    >
      <span
        className={cn(`rounded-l hover:bg-accent-muted hover:text-accent`, {
          [`${pageClass}`]: pageClass,
          "bg-base": pagination.currentPage !== pagination.totalPages,
          "!bg-inset !text-muted":
            pagination.currentPage === pagination.totalPages ||
            pagination.currentPage === 1,
        })}
      >
        <div className="h-5 w-5">{chevronLeft}</div>
      </span>
    </button>
  );

  const nextBtnView = (
    <button
      onClick={() =>
        pagination.currentPage !== pagination.totalPages
          ? onChangePage(pagination.currentPage + 1)
          : {}
      }
    >
      <span
        className={cn(`rounded-r hover:bg-accent-muted hover:text-accent`, {
          [`${pageClass}`]: pageClass,
          "bg-base": pagination.currentPage !== pagination.totalPages,
          "!bg-inset !text-muted":
            pagination.currentPage === pagination.totalPages,
        })}
      >
        <div className="h-5 w-5">{chevronRight}</div>
      </span>
    </button>
  );

  const getPageToShow = () => {
    return (
      <>
        {prevBtnView}
        {pagination.totalPages < 8 ? (
          <>
            {Array.from(
              { length: pagination.totalPages },
              (_, index) => index
            ).map((page) => (
              <span key={page}>{paginationBtnView(page + 1)}</span>
            ))}
          </>
        ) : (
          <>{renderPaginationButtons()}</>
        )}
        {nextBtnView}
      </>
    );
  };

  return (
    <div className="bg-subtle p-4 rounded-md ">
      <div className="w-full justify-between items-center grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="flex items-center text-muted col-span-1">
          <div className="h-6 w-6 pt-[0.5px]">{listIcon}</div>
          <p>{getPaginationMessage()}</p>
        </div>
        <div className="block md:flex items-center col-span-2 lg:place-content-end ">
          <div className="flex items-center mr-4">
            <label className="block pe-2">{t("row_per_page")}</label>
            <select
              className="p-1 border rounded shadow-sm focus:outline-none"
              value={pagination.perPageRows}
              onChange={onChangePerPageRows}
            >
              {PER_PAGE_ROW_LIST?.map((perPageRow) => (
                <option key={perPageRow} value={perPageRow}>
                  {perPageRow}
                </option>
              ))}
            </select>
          </div>
          <div className="inline-flex space-x-1.5">{getPageToShow()}</div>
        </div>
      </div>
    </div>
  );
};

export default PaginationFooter;
